import { PreviewState } from '../../../types/oc';
import { getDefaultActionStates, MESSAGE_ROLE, MessageType } from '../../Message/types/messages';

/**
 * Hardcoded OC system configuration
 */
export const OC_SYSTEM_CONFIG = {
  systemId: 'online-community',
  features: {
    reactions: true,
    reset: true,
    copyTracking: true,
  },
} as const;

/**
 * Default preview state for preview mode
 * Used when config.mode === 'preview' and no custom previewState is provided
 */
export const DEFAULT_PREVIEW_STATE: PreviewState = {
  authState: 'authenticated',
  usePreviewButton: false,
  messages: [],
  sessionHistory: [],
};

/**
 * Default OC UI configuration
 * Centralized defaults for all UI options to avoid repetitive nullish coalescing
 */
export const OC_DEFAULT_UI_CONFIG = {
  // UI Customization
  assistantName: 'AI Assistant',
  // title is computed dynamically based on assistantName
  // inputPlaceholder is computed dynamically based on assistantName
  welcomeMessage: 'Welcome! How can I help you today?',
  footerText: 'AI can make mistakes. Please verify important information.',
  showFeedbackButton: true,
  feedbackButtonText: 'Share Feedback',
  showAskHumanButton: true,
  createDraft: true,
  askHumanButtonText: 'Ask your fellow humans',
  askHumanPromptText: 'Need opinions? AI can help you post.',
  showHorizontalSources: true,
  showVerticalSources: true,
  loginTitle: 'Authentication Required',
  loginHelpText: 'Please log in to use the AI Assistant',
  loginButtonText: 'Log In',

  // Theme Configuration
  themeMode: 'light' as const,
  useSVGBackground: true,

  // Preview & Development
  autoOpen: false,

  // Widget Layout & Positioning
  fabPosition: { bottom: 22, right: 22 },
  panelBaseWidth: 800, // Default for non-history view, adjusted in context if history is shown
  panelMinWidth: 385, // Default for non-history view, adjusted in context if history is shown
  panelMaxWidth: 5000,
  showChatHistory: false,
  chatHistoryHeaderText: 'Chats',
  newChatButtonText: 'New Chat',
  clearChatButtonText: 'Clear Chat',
} as const;

/**
 * Default initial message for OC chat
 */
export const getOcDefaultInitialMessage = (
  customMessage?: string,
): MessageType => ({
  role: MESSAGE_ROLE.ASSISTANT,
  message: customMessage || OC_DEFAULT_UI_CONFIG.welcomeMessage || 'Welcome! How can I help you today?',
  messageId: 'oc-initial-message',
  actionStates: getDefaultActionStates(),
  localActionStates: getDefaultActionStates(),
  hideReactions: true,
});

/**
 * Check if error is an authentication error
 * Covers both 401 Unauthorized and 403 Forbidden responses
 */
export const isAuthError = (error: Error): boolean => {
  const message = error.message.toLowerCase();
  return (
    message.includes('401') ||
    message.includes('403') ||
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('authentication') ||
    message.includes('not authenticated')
  );
};

